// src/components/gateway/symbolGrid/SparklineRenderer.ts
// Lightweight Canvas2D sparkline renderer — replaces React SparklineChart
// for use in pooled row elements. Zero React overhead.

import { tokenAlpha } from '@vitesse/base-ui';
import { CHART_THEMES, calculatePaddingFactor } from './chartUtils';

export interface SparklineConfig {
  timeWindowMs: number;
  sampleRateHz: number;
  theme: string;
}

interface ColorScheme {
  line: string;
  gradientStart: string;
  gradientEnd: string;
}

const THEME_COLORS: Record<string, ColorScheme> = {};
for (const [name, colors] of Object.entries(CHART_THEMES)) {
  THEME_COLORS[name] = {
    line: colors.line,
    gradientStart: colors.gradient[0],
    gradientEnd: colors.gradient[1],
  };
}

// Fallback for an unknown/legacy theme name. frost is base-ui's default and is
// always present in CHART_THEMES, so there is nothing to guard against — the
// literals that used to sit here were a second copy of frost's palette that
// could never be reached, and would have gone stale silently if it changed.
const DEFAULT_COLORS: ColorScheme = THEME_COLORS.frost;

// --- Pre-allocated scratch buffers to eliminate per-frame GC pressure ---
// Shared across all sparklines (they render sequentially, not concurrently).
const MAX_POINTS = 2048;
const _scratchX = new Float64Array(MAX_POINTS);       // x coordinates
const _scratchY = new Float64Array(MAX_POINTS);       // y coordinates
const _scratchSegStart = new Uint16Array(128);         // segment start indices
const _scratchSegLen = new Uint16Array(128);           // segment lengths

// --- Per-canvas min/max cache: avoids O(buffer_size) scan when buffer hasn't changed ---
// Key: canvas element → { min, max, version }
const _minMaxCache = new WeakMap<HTMLCanvasElement, { min: number; max: number; version: number }>();

/**
 * Draw a sparkline chart onto a canvas element from raw TypedArray buffers.
 *
 * @param canvas     - Target canvas element (will be resized to fill parent)
 * @param timestamps - Float64Array of timestamps (circular buffer)
 * @param values     - Float64Array of values (circular buffer)
 * @param count      - Number of valid data points
 * @param config     - Chart configuration
 * @param head       - Start index in the circular buffer (oldest valid point)
 * @param capacity   - Total capacity of the circular buffer arrays
 */
export function drawSparkline(
  canvas: HTMLCanvasElement,
  timestamps: Float64Array,
  values: Float64Array,
  count: number,
  config: SparklineConfig,
  head: number = 0,
  capacity: number = count,
  minMaxVersion: number = -1,
  holdLast: boolean = false,
  nowMs: number = Date.now(),
  scaleMin: number = NaN,
  scaleMax: number = NaN,
): void {
  const parent = canvas.parentElement;
  if (!parent) return;

  const dpr = window.devicePixelRatio || 1;
  const width = parent.clientWidth;
  const height = parent.clientHeight;

  if (width <= 0 || height <= 0) return;

  // Only resize canvas when dimensions actually change (avoids GPU reallocation)
  const targetW = Math.round(width * dpr);
  const targetH = Math.round(height * dpr);
  if (canvas.width !== targetW || canvas.height !== targetH) {
    canvas.width = targetW;
    canvas.height = targetH;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);

  if (count < 1) {
    drawNoDataLine(ctx, width, height);
    return;
  }

  const colors = THEME_COLORS[config.theme] || DEFAULT_COLORS;
  // Anchor the window at the CONTINUOUS backend-frame now (clock.now(), passed
  // in as nowMs) — NOT Date.now() and NOT the discrete newest-slot timestamp.
  //   • Continuous (advances every 60fps frame) → the time axis flows left
  //     smoothly, instead of stepping once per 100ms slot.
  //   • Backend frame (Date.now()+offset) → backend-stamped samples sit at the
  //     right slot even under clock skew / staleness; the window covers the
  //     data by construction (the buffer was just advanced to this same nowMs).
  const now = nowMs;
  const startTime = now - config.timeWindowMs;
  const paddingFactor = calculatePaddingFactor(config.sampleRateHz);
  const paddingMs = config.timeWindowMs * paddingFactor;

  // Find min/max from points within the visible window for scaling.
  // Uses per-canvas cache keyed by buffer minMaxVersion to skip the O(buffer_size)
  // scan when no new samples have arrived (~12 frames between samples at 5Hz/60fps).
  let minVal = Infinity;
  let maxVal = -Infinity;
  let scaleCount = 0;

  if (isFinite(scaleMin) && isFinite(scaleMax)) {
    // STABLE scale supplied by the data source (expand-fast / contract-slow per
    // symbol). Use it verbatim instead of deriving the windowed min/max EVERY
    // frame — that per-frame rescan is what remapped (jumped/flattened) the whole
    // curve the instant another extreme entered or left the 10 s window, even
    // though the underlying samples never changed. While a feature is on screen
    // it's inside this range by construction, so it now holds a fixed height as it
    // scrolls; the range only eases when extremes come and go. scaleCount>0 since
    // the source only emits a finite range when the window has data.
    minVal = scaleMin;
    maxVal = scaleMax;
    scaleCount = 1;
    // Keep the per-canvas cache in sync (UN-padded, like the scan branch) so the
    // hover tooltip's getMinMaxCache() re-pads to the same range and its dot/value
    // land exactly on the drawn line.
    _minMaxCache.set(canvas, { min: scaleMin, max: scaleMax, version: minMaxVersion });
  } else {
    const cached = _minMaxCache.get(canvas);
    if (cached && cached.version === minMaxVersion && minMaxVersion >= 0) {
      // Buffer unchanged since last frame — reuse cached windowed min/max
      minVal = cached.min;
      maxVal = cached.max;
      scaleCount = 1; // we know there's data
    } else {
      // Buffer changed or first render — compute windowed min/max
      for (let i = 0; i < count; i++) {
        const idx = (head + i) % capacity;
        const ts = timestamps[idx];
        const v = values[idx];
        if (ts >= startTime && isFinite(v)) {
          if (v < minVal) minVal = v;
          if (v > maxVal) maxVal = v;
          scaleCount++;
        }
      }
      if (scaleCount > 0 && minMaxVersion >= 0) {
        _minMaxCache.set(canvas, { min: minVal, max: maxVal, version: minMaxVersion });
      }
    }
  }

  if (scaleCount === 0) {
    // Publish-on-change: no samples in the window means the value is HELD, not
    // missing. Opt-in callers (model variable charts) flatline at center instead
    // of showing the dashed "no data" line, so a steady symbol never goes blank.
    if (holdLast && count > 0) drawHeldLine(ctx, width, height, colors.line);
    else drawNoDataLine(ctx, width, height);
    return;
  }

  // Handle flat line (all same value) + add 5% vertical padding
  let range = maxVal - minVal;
  if (range === 0) {
    minVal -= 1;
    maxVal += 1;
    range = 2;
  } else {
    const yPad = range * 0.05;
    minVal -= yPad;
    maxVal += yPad;
    range = maxVal - minVal;
  }

  // Build ONE continuous segment into the pre-allocated scratch buffers.
  //
  // NO gap-cutting. Our values are PUBLISH-ON-CHANGE: a symbol only emits a new
  // sample when its value changes, so a long interval between two samples means
  // "the value held constant", NOT "data is missing". Cutting the line there
  // (the old `gapThreshold` behaviour) drew visible holes in the middle of a
  // chart whose data clearly existed — the tooltip happily followed the curve
  // across the hole. Connecting every sample in order draws the true signal;
  // the right-edge extension below carries the last value forward to `now`.
  let totalPoints = 0;
  const invTimeWindow = 1 / config.timeWindowMs;
  const invRange = 1 / range;

  for (let i = 0; i < count; i++) {
    const idx = (head + i) % capacity;
    const ts = timestamps[idx];
    const v = values[idx];

    // Filter to visible window + padding
    if (ts < startTime - paddingMs) continue;
    if (!isFinite(v)) continue;

    if (totalPoints < MAX_POINTS) {
      _scratchX[totalPoints] = width * (1 - (now - ts) * invTimeWindow);
      _scratchY[totalPoints] = height - (v - minVal) * invRange * height;
      totalPoints++;
    }
  }

  let numSegments = 0;
  if (totalPoints > 0) {
    _scratchSegStart[0] = 0;
    _scratchSegLen[0] = totalPoints;
    numSegments = 1;
  }

  if (numSegments === 0) {
    drawNoDataLine(ctx, width, height);
    return;
  }

  // Extend last segment to right edge (prevents jitter from Date.now oscillation)
  const lastSegIdx = numSegments - 1;
  const lastSegEnd = _scratchSegStart[lastSegIdx] + _scratchSegLen[lastSegIdx] - 1;
  if (_scratchX[lastSegEnd] < width && totalPoints < MAX_POINTS) {
    _scratchX[totalPoints] = width;
    _scratchY[totalPoints] = _scratchY[lastSegEnd];
    totalPoints++;
    _scratchSegLen[lastSegIdx]++;
  }

  // Draw each segment from scratch buffers
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, hexToRgba(colors.gradientStart, 0.25));
  gradient.addColorStop(1, hexToRgba(colors.gradientEnd, 0.02));

  for (let s = 0; s < numSegments; s++) {
    const start = _scratchSegStart[s];
    const len = _scratchSegLen[s];
    if (len < 1) continue;

    // Opt-in (holdLast): hold the first value back to the left edge so sparse
    // publish-on-change data never leaves a blank left section / jumps as the
    // oldest point scrolls off. Off (symbol grid) → start at the first point,
    // unchanged.
    const startY = _scratchY[start];
    const leftX = holdLast && s === 0 && _scratchX[start] > 0 ? 0 : _scratchX[start];

    // Fill (gradient area under the line)
    if (len > 1) {
      ctx.beginPath();
      ctx.moveTo(leftX, height);
      ctx.lineTo(leftX, startY);
      for (let j = start; j < start + len; j++) {
        ctx.lineTo(_scratchX[j], _scratchY[j]);
      }
      ctx.lineTo(_scratchX[start + len - 1], height);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();
    }

    // Line
    ctx.beginPath();
    ctx.moveTo(leftX, startY);
    for (let j = start; j < start + len; j++) {
      ctx.lineTo(_scratchX[j], _scratchY[j]);
    }
    ctx.strokeStyle = colors.line;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.stroke();
  }
}

/**
 * Draw a solid flat line at vertical center — a value held since before the
 * visible window (publish-on-change → flatline, never "no data").
 */
function drawHeldLine(ctx: CanvasRenderingContext2D, width: number, height: number, color: string): void {
  const y = height / 2;
  ctx.beginPath();
  ctx.moveTo(0, y);
  ctx.lineTo(width, y);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.stroke();
}

/**
 * Draw a dashed "no data" line in the center.
 */
function drawNoDataLine(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const y = height / 2;
  ctx.beginPath();
  ctx.setLineDash([3, 3]);
  ctx.moveTo(4, y);
  ctx.lineTo(width - 4, y);
  ctx.strokeStyle = tokenAlpha('--overlay-color', 0.15);
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.setLineDash([]);
}

/**
 * Clear a canvas completely.
 */
export function clearSparkline(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }
}

/**
 * Get the cached min/max values for a canvas (from the last drawSparkline call).
 * Returns { min, max, version } or null if no cache entry exists.
 * The min/max are the padded values used for Y-axis scaling.
 */
export function getMinMaxCache(canvas: HTMLCanvasElement): { min: number; max: number; version: number } | null {
  return _minMaxCache.get(canvas) ?? null;
}

/**
 * Draw a tooltip crosshair line on top of an already-rendered sparkline.
 * Only draws the vertical dashed line — the circle marker is a DOM element
 * positioned by SymbolGrid so it can escape cell overflow boundaries.
 *
 * @param canvas    - The sparkline canvas (already rendered)
 * @param x         - CSS-pixel x coordinate of the crosshair
 */
export function drawTooltipOverlay(
  canvas: HTMLCanvasElement,
  x: number,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const parent = canvas.parentElement;
  if (!parent) return;

  const dpr = window.devicePixelRatio || 1;
  const height = parent.clientHeight;

  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // Vertical crosshair line
  ctx.beginPath();
  ctx.setLineDash([3, 3]);
  ctx.moveTo(x, 0);
  ctx.lineTo(x, height);
  ctx.strokeStyle = tokenAlpha('--overlay-color', 0.3);
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.restore();
}

/**
 * Get the line color for a given theme (for tooltip overlay).
 */
export function getThemeLineColor(theme: string): string {
  return (THEME_COLORS[theme] || DEFAULT_COLORS).line;
}

/**
 * Convert hex color to rgba string. Cached to avoid repeated string parsing.
 */
const _hexToRgbaCache = new Map<string, string>();
function hexToRgba(hex: string, alpha: number): string {
  const key = hex + alpha;
  let result = _hexToRgbaCache.get(key);
  if (result) return result;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  result = `rgba(${r}, ${g}, ${b}, ${alpha})`;
  _hexToRgbaCache.set(key, result);
  return result;
}
